/**
 * Same dateFrom/dateTo derivation as {@link SessionsDataTable},
 * then YYYY-MM-DD (local timezone) for GET /dashboard/stats.
 */
import type { DashboardStatsQuery } from '@/types/dashboard.types';
import { toLocalDateString } from '@/utils/format-date-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';

/** Preset range for dashboard stats (`all` skips dateFrom / dateTo). */
export type DashboardStatsPeriod = 'all' | 'today' | 'week' | 'month' | 'year';

/** UI comparison period (mapped to API `compare` via {@link ApiComparePeriod}). */
export type ComparisonPeriod =
  | 'none'
  | 'previous_week'
  | 'previous_month'
  | 'previous_quarter'
  | 'previous_year';

/** Values accepted by the backend `compare` query param. */
export type ApiComparePeriod = 'week' | 'month' | 'year' | 'trim';

/** Dashboard stats toolbar state (preset / custom dates + compare). */
export interface DashboardStatsDateFilterState {
  dateRange: DashboardStatsPeriod;
  customDateFrom?: Date;
  customDateTo?: Date;
  establishmentIds?: string[];
  comparisonPeriod: ComparisonPeriod;
}

/** Default toolbar: all time, no comparison. */
export const DEFAULT_DASHBOARD_STATS_DATE_FILTERS: DashboardStatsDateFilterState = {
  dateRange: 'all',
  customDateFrom: undefined,
  customDateTo: undefined,
  establishmentIds: undefined,
  comparisonPeriod: 'none',
};

/** Map UI comparison period to the API `compare` param. */
function mapComparisonToApiParam(comparisonPeriod: ComparisonPeriod): ApiComparePeriod | undefined {
  switch (comparisonPeriod) {
    case 'previous_week':
      return 'week';
    case 'previous_month':
      return 'month';
    case 'previous_quarter':
      return 'trim';
    case 'previous_year':
      return 'year';
    default:
      return undefined;
  }
}

/** Resolve a dashboard stats preset to local dateFrom / dateTo bounds. */
function resolvePresetRange(dateRange: DashboardStatsPeriod): { dateFrom?: Date; dateTo?: Date } {
  if (
    dateRange !== 'today' &&
    dateRange !== 'week' &&
    dateRange !== 'month' &&
    dateRange !== 'year'
  ) {
    return {};
  }
  return resolveListDateRange({ dateRange }, { periodEnd: 'endOfPeriod' });
}

/** Build a dashboard stats query object, or undefined when every field is empty. */
function buildStatsQuery(
  dateFrom: Date | undefined,
  dateTo: Date | undefined,
  establishmentIds: string[] | undefined,
  compare: ApiComparePeriod | undefined,
): DashboardStatsQuery | undefined {
  const out: DashboardStatsQuery = {};
  if (dateFrom) out.dateFrom = toLocalDateString(dateFrom);
  if (dateTo) out.dateTo = toLocalDateString(dateTo);
  if (establishmentIds?.length) out.establishmentIds = establishmentIds;
  if (compare) out.compare = compare;
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Query API dashboard/stats (optional dateFrom / dateTo). */
export function buildDashboardStatsDateQuery(
  filters: DashboardStatsDateFilterState,
): DashboardStatsQuery | undefined {
  const compare = mapComparisonToApiParam(filters.comparisonPeriod);

  if (filters.customDateFrom || filters.customDateTo) {
    return buildStatsQuery(
      filters.customDateFrom,
      filters.customDateTo,
      filters.establishmentIds,
      compare,
    );
  }

  if (filters.dateRange === 'all') {
    return buildStatsQuery(undefined, undefined, filters.establishmentIds, compare);
  }

  const { dateFrom, dateTo } = resolvePresetRange(filters.dateRange);
  return buildStatsQuery(dateFrom, dateTo, filters.establishmentIds, compare);
}
